import { useContext, useEffect, useMemo, useState, type ReactElement } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import AdminLogin from "./AdminLogin";
import AdminNotFound from "../pages/AdminNotFound";
import AdminErrorState from "./AdminErrorState";
import GlobalSearchBar from "./GlobalSearchBar";
import useAdminSessionQuery from "../hooks/useAdminSessionQuery";
import { AdminSessionContext } from "../context/AdminSessionContext";
import { ToastProvider } from "../context/ToastContext";
import { hasRoleAtLeast, resolvePrimaryRole } from "../utils/roleHelpers";
import { hasAnyPermission, hasPermission, type PermissionCode } from "../utils/permissionHelpers";
import AdminWorkspaceOps from "../pages/AdminWorkspaceOps";
import AdminWorkspaceContent from "../pages/AdminWorkspaceContent";
import AdminWorkspaceUsers from "../pages/AdminWorkspaceUsers";
import AdminFinance from "../pages/AdminFinance";
import AdminWorkspacePlatform from "../pages/AdminWorkspacePlatform";

type WorkspaceNavItem = {
  key: "ops" | "content" | "users" | "finance" | "platform";
  label: string;
  caption: string;
  to: string;
  permissions: PermissionCode[];
};

const WORKSPACE_NAV_ITEMS: WorkspaceNavItem[] = [
  { key: "ops", label: "Ops", caption: "Watch & Respond", to: "/admin/ops", permissions: ["Dashboard.View"] },
  { key: "content", label: "Content 360", caption: "Series & Queue", to: "/admin/content-360", permissions: ["Content.View"] },
  { key: "users", label: "User 360", caption: "Identity & Actions", to: "/admin/users-360", permissions: ["Users.View", "Comments.View"] },
  { key: "finance", label: "Finance", caption: "Ledger & Payouts", to: "/admin/finance", permissions: ["Finance.View", "Finance.Ledger.View", "Revenue.View"] },
  {
    key: "platform",
    label: "Platform",
    caption: "Settings & Policy",
    to: "/admin/platform",
    permissions: ["Economy.View", "ZTokens.View", "SiteSettings.Manage", "Permissions.View", "Notifications.View", "Feedback.View", "Gamification.View"],
  },
];

const CREATOR_WORKSPACE_WHITELIST = new Set<WorkspaceNavItem["key"]>(["content"]);

function WorkspaceGlyph({ itemKey }: { itemKey: WorkspaceNavItem["key"] }) {
  const glyphMap: Record<WorkspaceNavItem["key"], string> = {
    ops: "OP",
    content: "CT",
    users: "US",
    finance: "FN",
    platform: "PF",
  };

  return (
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-white/25 bg-white/10 text-[11px] font-extrabold tracking-wide text-white">
      {glyphMap[itemKey]}
    </span>
  );
}

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionQuery = useAdminSessionQuery();
  const [navOpen, setNavOpen] = useState(false);

  const contextValue = useMemo(
    () => ({ loading: sessionQuery.isLoading, session: sessionQuery.data ?? null }),
    [sessionQuery.isLoading, sessionQuery.data],
  );

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!location.search || location.search.length < 2) {
      return;
    }

    const rawTokens = location.search.slice(1).split("&");
    const malformedTabToken = rawTokens.find((token) => token.startsWith("tab-") && !token.includes("="));
    if (!malformedTabToken) {
      return;
    }

    const tabValue = malformedTabToken.slice(4).trim();
    if (!tabValue) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (params.has("tab")) {
      return;
    }

    params.delete(malformedTabToken);
    params.set("tab", tabValue);
    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);

  if (sessionQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="workspace-panel rounded-2xl px-6 py-4 text-sm text-slate-700">Yonetim oturumu dogrulaniyor...</div>
      </div>
    );
  }

  const sessionData = sessionQuery.data;
  if (!sessionData) {
    return (
      <AdminLogin
        title="Yonetim Girisi"
        description="Yetkili hesap bilgileri ile devam edin."
        onLoginSuccess={() => {
          void sessionQuery.mutate();
        }}
      />
    );
  }

  const currentPermissions = sessionData.permissions ?? [];
  const primaryRole = resolvePrimaryRole(sessionData.roles);
  const isAdminOrAbove = hasRoleAtLeast(sessionData.roles, "admin");
  const isModeratorOrAbove = hasRoleAtLeast(sessionData.roles, "moderator");
  const isCreator = hasRoleAtLeast(sessionData.roles, "creator");
  const creatorRestricted = isCreator && !isModeratorOrAbove;
  const allowRoleFallback = isAdminOrAbove;

  const hasAnyWorkspace = WORKSPACE_NAV_ITEMS.some((item) => hasAnyPermission(currentPermissions, item.permissions));
  const hasPanelAccess = isModeratorOrAbove
    ? hasAnyWorkspace || allowRoleFallback
    : creatorRestricted && hasPermission(currentPermissions, "Content.View");

  if (!hasPanelAccess) {
    return <AdminNotFound />;
  }

  const visibleWorkspaces = WORKSPACE_NAV_ITEMS.filter((item) => {
    if (creatorRestricted && !CREATOR_WORKSPACE_WHITELIST.has(item.key)) {
      return false;
    }
    if (allowRoleFallback) {
      return true;
    }
    return hasAnyPermission(currentPermissions, item.permissions);
  });

  const activeWorkspace = visibleWorkspaces.find((item) => location.pathname.startsWith(item.to)) ?? visibleWorkspaces[0];
  const defaultRoute = creatorRestricted ? "/admin/content-360" : "/admin/ops";
  const identityLabel = sessionData.name || "Operator";
  const permissionCount = currentPermissions.length;

  return (
    <AdminSessionContext.Provider value={contextValue}>
      <ToastProvider>
        <div className="min-h-screen px-3 py-3 md:px-5 md:py-5">
          <div className="admin-app-bg flex min-h-[calc(100vh-24px)] overflow-hidden rounded-[28px]">
            <aside
              className={`admin-sidebar z-20 w-[280px] shrink-0 px-4 py-5 transition-transform duration-200 ${
                navOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
              } fixed inset-y-3 left-3 rounded-2xl md:static md:inset-auto md:left-auto md:rounded-none`}
            >
              <div className="flex h-full flex-col">
                <div className="mb-4 rounded-2xl border border-white/15 bg-white/5 px-4 py-4">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-sky-100/80">Zenitoon</div>
                  <div className="mt-1 text-xl font-extrabold tracking-tight text-white">Admin vNext</div>
                  <div className="mt-2 text-xs text-blue-100/80">Workspace 360 operasyon konsolu</div>
                </div>

                <div className="mb-4 rounded-2xl border border-white/15 bg-white/5 px-4 py-3">
                  <div className="text-xs text-blue-100/70">Aktif kullanici</div>
                  <div className="mt-1 truncate text-sm font-semibold text-white">{identityLabel}</div>
                  <div className="mt-1 text-xs text-blue-100/80">Rol: {primaryRole || "moderator"}</div>
                  <div className="mt-2 text-xs text-blue-100/70">Izin sayisi: {permissionCount}</div>
                </div>

                <nav className="flex-1 space-y-2 overflow-y-auto admin-scroll-area pr-1">
                  {visibleWorkspaces.map((item) => {
                    const active = activeWorkspace?.key === item.key;
                    return (
                      <Link
                        key={item.key}
                        to={item.to}
                        className={`admin-sidebar-link flex items-center gap-3 rounded-2xl px-3 py-3 ${active ? "admin-sidebar-link-active" : ""}`}
                      >
                        <WorkspaceGlyph itemKey={item.key} />
                        <span className="flex flex-col">
                          <span className="text-sm font-semibold leading-tight">{item.label}</span>
                          <span className="text-[11px] text-blue-100/75">{item.caption}</span>
                        </span>
                      </Link>
                    );
                  })}
                </nav>

                <button
                  type="button"
                  onClick={() => setNavOpen(false)}
                  className="mt-3 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-semibold text-white md:hidden"
                >
                  Menuyu kapat
                </button>
              </div>
            </aside>

            {navOpen && (
              <button
                type="button"
                aria-label="Sidebar overlay"
                className="fixed inset-0 z-10 bg-slate-900/35 backdrop-blur-sm md:hidden"
                onClick={() => setNavOpen(false)}
              />
            )}

            <div className="relative flex min-h-[calc(100vh-24px)] flex-1 flex-col md:ml-0">
              <header className="border-b border-slate-200/70 bg-white/55 px-4 py-3 backdrop-blur-md md:px-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setNavOpen(true)}
                      className="rounded-xl border border-slate-300/80 bg-white/80 px-3 py-2 text-xs font-semibold text-slate-700 md:hidden"
                    >
                      Workspace
                    </button>
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Current workspace</div>
                      <div className="text-lg font-bold text-slate-900">{activeWorkspace?.label ?? "Admin"}</div>
                    </div>
                  </div>
                  <div className="admin-pill">Role: {primaryRole || "moderator"}</div>
                  {visibleWorkspaces.length > 1 && (
                    <select
                      aria-label="Workspace hizli gecis"
                      value={activeWorkspace?.key ?? visibleWorkspaces[0]?.key}
                      onChange={(event) => {
                        const next = visibleWorkspaces.find((item) => item.key === event.target.value);
                        if (!next) {
                          return;
                        }
                        navigate(next.to);
                      }}
                      className="rounded-xl border border-slate-300/80 bg-white/80 px-3 py-2 text-xs font-semibold text-slate-700"
                    >
                      {visibleWorkspaces.map((item) => (
                        <option key={item.key} value={item.key}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </header>

              <main className="admin-scroll-area flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-6">
                <div className="space-y-4">
                  {!creatorRestricted && (
                    <div className="admin-command-card p-3">
                      <GlobalSearchBar />
                    </div>
                  )}
                  <Routes>
                    <Route
                      path="ops"
                      element={<AnyPermissionRoute permissions={["Dashboard.View"]} component={<AdminWorkspaceOps />} />}
                    />
                    <Route
                      path="content-360"
                      element={<AnyPermissionRoute permissions={["Content.View"]} component={<AdminWorkspaceContent />} />}
                    />
                    <Route
                      path="users-360"
                      element={<AnyPermissionRoute permissions={["Users.View", "Comments.View"]} component={<AdminWorkspaceUsers />} />}
                    />
                    <Route
                      path="finance"
                      element={<AnyPermissionRoute permissions={["Finance.View", "Finance.Ledger.View", "Revenue.View"]} component={<AdminFinance />} />}
                    />
                    <Route
                      path="platform"
                      element={
                        <AnyPermissionRoute
                          permissions={[
                            "Economy.View",
                            "ZTokens.View",
                            "SiteSettings.Manage",
                            "Permissions.View",
                            "Notifications.View",
                            "Feedback.View",
                            "Gamification.View",
                          ]}
                          component={<AdminWorkspacePlatform />}
                        />
                      }
                    />

                    <Route path="dashboard" element={<Navigate to="/admin/ops?tab=dashboard" replace />} />
                    <Route path="ops-center" element={<Navigate to="/admin/ops?tab=ops-center" replace />} />
                    <Route path="content" element={<Navigate to="/admin/content-360?tab=content" replace />} />
                    <Route path="content-management" element={<Navigate to="/admin/content-360?tab=content" replace />} />
                    <Route path="contracts" element={<Navigate to="/admin/content-360?tab=contracts" replace />} />
                    <Route path="users" element={<Navigate to="/admin/users-360?tab=users" replace />} />
                    <Route path="user-management" element={<Navigate to="/admin/users-360?tab=users" replace />} />
                    <Route path="users-transactions" element={<Navigate to="/admin/users-360?tab=transactions" replace />} />
                    <Route path="comments" element={<Navigate to="/admin/users-360?tab=comments" replace />} />
                    <Route path="revenue" element={<Navigate to="/admin/finance?tab=sales-ledger" replace />} />
                    <Route path="royalties" element={<Navigate to="/admin/finance?tab=creator-accruals" replace />} />
                    <Route path="creators" element={<Navigate to="/admin/finance?tab=creator-accruals" replace />} />
                    <Route path="payouts" element={<Navigate to="/admin/finance?tab=payout-queue" replace />} />
                    <Route path="economy" element={<Navigate to="/admin/platform?tab=economy" replace />} />
                    <Route path="z-token-packages" element={<Navigate to="/admin/platform?tab=z-token-packages" replace />} />
                    <Route path="site-settings" element={<Navigate to="/admin/platform?tab=site-settings" replace />} />
                    <Route path="permissions" element={<Navigate to="/admin/platform?tab=permissions" replace />} />
                    <Route path="notifications" element={<Navigate to="/admin/platform?tab=notifications" replace />} />
                    <Route path="ecosystem" element={<Navigate to="/admin/platform?tab=ecosystem" replace />} />
                    <Route path="resend" element={<Navigate to="/admin/platform?tab=resend" replace />} />
                    <Route path="access-ui" element={<Navigate to="/admin/platform?tab=access-ui" replace />} />
                    <Route path="access-ui-modals" element={<Navigate to="/admin/platform?tab=access-ui-modals" replace />} />
                    <Route path="feedback" element={<Navigate to="/admin/platform?tab=feedback" replace />} />
                    <Route path="gamification" element={<Navigate to="/admin/platform?tab=gamification" replace />} />
                    <Route path="alerts" element={<Navigate to="/admin/ops?tab=alerts" replace />} />
                    <Route path="logs" element={<Navigate to="/admin/ops?tab=logs" replace />} />
                    <Route path="help" element={<Navigate to="/admin/platform?tab=help" replace />} />

                    <Route index element={<Navigate to={defaultRoute} replace />} />
                    <Route path="*" element={creatorRestricted ? <Navigate to="/admin/content-360" replace /> : <AdminNotFound />} />
                  </Routes>
                </div>
              </main>
            </div>
          </div>
        </div>
      </ToastProvider>
    </AdminSessionContext.Provider>
  );
}

function AnyPermissionRoute({
  permissions,
  component,
}: {
  permissions: PermissionCode[];
  component: ReactElement;
}) {
  const { loading, session } = useContext(AdminSessionContext);

  if (loading) {
    return <div className="workspace-panel rounded-2xl px-6 py-5 text-sm text-slate-600">Yukleniyor...</div>;
  }

  if (!session) {
    return (
      <AdminErrorState
        status="warning"
        title="Bu bölüm için iznin yok"
        description="İstenen işleme erişim vermek için rol veya permission tanımı güncellenmeli."
        helpHref="/admin/platform?tab=help"
      />
    );
  }

  if (!hasAnyPermission(session.permissions, permissions) && !hasRoleAtLeast(session.roles, "admin")) {
    return (
      <AdminErrorState
        status="warning"
        title="Bu bölüm için iznin yok"
        description="İstenen işleme erişim vermek için rol veya permission tanımı güncellenmeli."
        helpHref="/admin/platform?tab=help"
      />
    );
  }

  return component;
}

